// Or from '@reduxjs/toolkit/query' if not using the auto-generated hooks
import { BaseQueryFn, FetchArgs, FetchBaseQueryError, createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { apiRequest, msalInstance } from '../auth/azureConfig';
import { sessionExpired } from '../store/slices/auth.slice';

let currentAccessToken: string | null = null;

export const setAccessToken = (token: string | null) => {
  currentAccessToken = token;
  console.log('Access token updated:', token ? 'SET' : 'CLEARED');
};

/**
 * Resolve a fresh Azure token for an outgoing request (the MSAL-recommended interceptor pattern).
 */
export const getFreshAccessToken = async (): Promise<string | null> => {
  // Local development bypass — LOCAL_DEV_MODE is not a real Azure token.
  if (currentAccessToken === 'LOCAL_DEV_MODE') {
    return currentAccessToken;
  }

  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (!account) {
    return null;
  }

  try {
    const { idToken, accessToken } = await msalInstance.acquireTokenSilent({ scopes: apiRequest.scopes, account });
    // For Azure AD B2C the ID token usually carries the right audience; fall back to the access token.
    return idToken || accessToken;
  } catch (error) {
    console.error('Silent token refresh failed:', error);
    return null;
  }
};

export const setHeaders = async (): Promise<Record<string, string>> => {
  const accessToken = await getFreshAccessToken();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  } else {
    console.warn('No access token available for headers');
  }
  return headers;
};

// Environment-specific API URL configuration
const getApiBaseUrl = () => {
  // First try explicit env var (highest priority)
  if (process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL;
  }

  // Use build-time environment to determine API URL
  const environment = process.env.REACT_APP_ENVIRONMENT || 'development';

  switch (environment) {
    case 'demo':
      return 'https://stellardemo.bio-rad.com';
    case 'production':
      return 'https://stellar.bio-rad.com';
    case 'test':
      return 'https://stellarqa.bio-rad.com';
    case 'development':
    default:
      return 'http://localhost:8080';
  }
};

export const API_BASE_URL = getApiBaseUrl();
console.log('API_BASE_URL:', API_BASE_URL, '(environment:', process.env.REACT_APP_ENVIRONMENT || 'development', ')');

const rawBaseQuery = fetchBaseQuery({
  baseUrl: API_BASE_URL,
  prepareHeaders: async (headers, { endpoint }) => {
    // resolve a fresh token per request so a long-lived session doesn't send a stale, expired bearer
    const token = await getFreshAccessToken();
    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }

    // Don't set Content-Type for uploads - let browser handle it
    if (!endpoint?.includes('upload')) {
      headers.set('content-type', 'application/json');
    }

    return headers;
  },
});

/**
 * Wrap the base query to detect an expired session (401) and signal the app to recover.
 */
const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (args, api, extraOptions) => {
  const result = await rawBaseQuery(args, api, extraOptions);
  if (result.error?.status === 401) {
    api.dispatch(sessionExpired());
  }
  return result;
};

// initialize an empty api service that we'll inject endpoints into later as needed
export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  endpoints: () => ({}),
});
console.log('baseApi created successfully');
